import cx from 'classnames'
import { type KeenSliderInstance } from 'keen-slider'
import { useContext } from 'react'

import { StringsContext } from '@lib/strings-context'

import GalleryCarouselArrowLeft from './arrow-left'
import GalleryCarouselArrowRight from './arrow-right'
import GalleryCarouselCounter from './counter'
import GalleryCarouselDot from './dot'

interface GalleryCarouselControlsProps {
  slider: KeenSliderInstance | null
  currentSlide: number
  slideCount: number
  id?: string
  hasArrows?: boolean
  hasDots?: boolean
  hasCounter?: boolean
  cornerControls?: boolean
}

const GalleryCarouselControls = ({
  slider,
  currentSlide,
  slideCount,
  id,
  hasArrows,
  hasDots,
  hasCounter,
  cornerControls,
}: GalleryCarouselControlsProps) => {
  const strings = useContext(StringsContext)

  return (
    <div
      className={cx(
        'flex absolute bottom-0 inset-x-0 transform pointer-events-none',
        {
          'justify-center translate-y-1/2': !cornerControls,
          'justify-start translate-y-0': cornerControls,
        },
      )}
    >
      <div
        className={cx(
          'flex flex-row items-center rounded-full p-1 pointer-events-auto',
          {
            'relative bg-pageBG border': !cornerControls,
            'static bg-transparent border-none': cornerControls,
          },
        )}
      >
        {hasArrows && (
          <GalleryCarouselArrowLeft
            slider={slider}
            label={strings.carouselLeftArrowLabel}
            id={id}
            cornerControls={cornerControls}
          />
        )}

        <div
          className={cx('flex items-center justify-center relative h-8 mx-2', {
            'mx-0 pl-1 pb-3': cornerControls,
          })}
        >
          {hasDots && (
            <div
              className={cx('relative flex items-center h-8', {
                'ml-3': cornerControls,
              })}
            >
              {[...Array(slideCount).keys()].map((index) => (
                <GalleryCarouselDot
                  key={index}
                  index={index}
                  label={strings.carouselDotLabel}
                  slider={slider}
                  currentSlide={currentSlide}
                />
              ))}
            </div>
          )}

          {hasCounter && (
            <GalleryCarouselCounter
              currentSlide={currentSlide}
              slideCount={slideCount}
              counterText={strings.carouselCounterText}
              cornerControls={cornerControls}
            />
          )}
        </div>

        {hasArrows && (
          <GalleryCarouselArrowRight
            slider={slider}
            label={strings.carouselRightArrowLabel}
            id={id}
            cornerControls={cornerControls}
          />
        )}
      </div>
    </div>
  )
}

export default GalleryCarouselControls
